/**
 * Grand Quack Auto — vehicle rescue / pose-reset helpers.
 *
 * Extracted from vehicleSystem.ts so the system file stays under the
 * project's 200-line cap. Pure car-pose mutation: pose validation,
 * stuck detection, and a Y-lift / teleport reset. No DOM, no input.
 */

import * as CANNON from 'cannon-es';
import { VEHICLE } from '../config';
import type { VehicleRef } from '../types';
import { buildingDefs } from '../world/cityGenerator';

const _upWorld = new CANNON.Vec3();
const _worldUp = new CANNON.Vec3(0, 1, 0);

// ── Displacement-based stuck detection ───────────────────────────────────
// Tracks whether the active car is making progress by sampling position
// every 0.5 s. More reliable than speed checks — wheels spinning against
// a wall can report velocity without actual displacement.
let lastCheckPos = new CANNON.Vec3();
let lastCheckTime = 0;
let stuckAccum = 0;

/** Reset stuck tracking (call on car enter / after teleport). */
export function resetStuckTracking(pos: CANNON.Vec3, time: number): void {
  lastCheckPos.copy(pos);
  lastCheckTime = time;
  stuckAccum = 0;
}

/**
 * Returns true when the car has been stuck for > 2 s. Must be called every
 * fixedUpdate with `hasInput = true` to accumulate. Resets automatically
 * when progress is detected.
 */
export function updateStuckCheck(pos: CANNON.Vec3, time: number, hasInput: boolean): boolean {
  if (!hasInput) { stuckAccum = 0; return false; }
  const elapsed = time - lastCheckTime;
  if (elapsed < 0.5) return false; // sample every 0.5 s
  const dx = pos.x - lastCheckPos.x;
  const dz = pos.z - lastCheckPos.z;
  const disp = Math.sqrt(dx * dx + dz * dz);
  if (disp < 0.5) {
    stuckAccum += elapsed;
  } else {
    stuckAccum = 0;
  }
  lastCheckPos.copy(pos);
  lastCheckTime = time;
  return stuckAccum > 2.0;
}

/**
 * Reset a stuck/flipped car to a clean drivable pose. Called on every entry
 * so the player can always recover regardless of how the car was left.
 * With teleport=true, also snaps the chassis back to its original spawn —
 * use when the car is wedged inside geometry and a Y-lift alone won't free it.
 *
 * Note: callers that track their own steering state must reset it separately;
 * this helper only clears wheel-level engine/brake/steering values.
 */
export function resetCarPose(car: VehicleRef, teleport: boolean = false): void {
  const body = car.chassisBody;
  if (teleport) {
    body.position.set(
      car.originalSpawn[0],
      car.originalSpawn[1],
      car.originalSpawn[2],
    );
  } else {
    const cleanY = VEHICLE.chassisHalfExtents[1]
      + VEHICLE.suspensionRestLength
      + VEHICLE.wheelRadius
      + 0.2;
    body.position.y = Math.max(body.position.y, cleanY);
  }
  body.quaternion.set(0, 0, 0, 1);
  body.velocity.set(0, 0, 0);
  body.angularVelocity.set(0, 0, 0);
  // Clear any residual engine/brake force on the wheels so the car doesn't
  // immediately squirt away from its reset position.
  for (let i = 0; i < car.vehicle.wheelInfos.length; i++) {
    car.vehicle.applyEngineForce(0, i);
    car.vehicle.setBrake(0, i);
    car.vehicle.setSteeringValue(0, i);
  }
}

/**
 * True if the car chassis AABB overlaps any building AABB in the XZ plane.
 * Used to detect cars wedged inside buildings that physics alone won't free.
 * No padding — building layout (inset sub-cells, ≤0.5 unit gaps) prevents
 * wedging. Padding caused false positives for cars parked normally on roads.
 */
export function carOverlapsBuilding(car: VehicleRef): boolean {
  const [hx, , hz] = VEHICLE.chassisHalfExtents;
  const pos = car.chassisBody.position;
  for (const b of buildingDefs) {
    const bHalfW = b.w / 2;
    const bHalfD = b.d / 2;
    if (pos.x + hx > b.x - bHalfW && pos.x - hx < b.x + bHalfW &&
        pos.z + hz > b.z - bHalfD && pos.z - hz < b.z + bHalfD) {
      return true;
    }
  }
  return false;
}

/**
 * True if a car has fallen through its suspension (chassis near ground),
 * flipped, or is wedged inside a building. Used to auto-rescue parked cars.
 */
export function carIsStuck(car: VehicleRef): boolean {
  const body = car.chassisBody;
  body.quaternion.vmult(_worldUp, _upWorld);
  if (_upWorld.y < 0.3) return true; // flipped
  const minHealthyY = VEHICLE.chassisHalfExtents[1] + VEHICLE.wheelRadius * 0.5;
  if (body.position.y < minHealthyY) return true;
  if (carOverlapsBuilding(car)) return true; // wedged in building
  return false;
}
